import { SparklesIcon } from "@heroicons/react/24/outline";

import { DropdownMenuItem } from "@fronterahealth/frontera-ui-components/ui/dropdown-menu";

import { ApiReportReportTypeChoices, ReportSectionsType } from "@api/graphql/types-and-hooks";
import { ConfirmationDialog } from "@components/ConfirmationDialog/ConfirmationDialog";
import { useRegenerateSection } from "@components/ReportField/ReportSection/useRegenerateSection";
import { useReport } from "@providers/ReportProvider";

interface ItemProps {
  section: ReportSectionsType;
  onOpen: () => void;
}

// Assessment V2 regenerates directly; the Evaluation step viewer confirms
// first since regeneration replaces existing content irreversibly.
export const ReportSectionActionMenuRegenerateItem: React.FC<ItemProps> = ({ section, onOpen }) => {
  const { report, viewMode } = useReport();
  const requiresConfirmation =
    report.reportType === ApiReportReportTypeChoices.DiagnosisEvaluation && viewMode === "step";

  const { mutate: startWorkflowMutate } = useRegenerateSection();

  return (
    <DropdownMenuItem
      className="text-blurple-600"
      onClick={() => (requiresConfirmation ? onOpen() : startWorkflowMutate({ sectionId: section.id }))}
    >
      <SparklesIcon />
      Regenerate
    </DropdownMenuItem>
  );
};

interface DialogProps {
  section: ReportSectionsType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ReportSectionActionMenuRegenerateDialog: React.FC<DialogProps> = ({ section, open, onOpenChange }) => {
  const { mutate: startWorkflowMutate, isPending } = useRegenerateSection({
    onSettled: () => {
      onOpenChange(false);
    },
  });

  return (
    <ConfirmationDialog
      open={open}
      title="Regenerate this section?"
      content="This will replace any existing content in the section, including anything you've added or edited. This action is irreversible."
      submitButtonText="Yes, Regenerate"
      submitButtonLoading={isPending}
      cancelButtonText="No, Cancel"
      submitButtonAction="destructive"
      onSubmitClick={() => startWorkflowMutate({ sectionId: section.id })}
      onCancelClick={() => onOpenChange(false)}
    />
  );
};
